import { Box, Toolbar } from "@mui/material";
import { Outlet } from "react-router-dom";
import { Header } from "./Containers/Header";
import { useIsAuthenticated } from "@azure/msal-react";
import { useGetAuthorizationQuery } from "Services/Authorization";
import { AuthContext } from "./Context";
import { useMainLayout } from "./Hooks";
import { NotificationDrawer } from "Containers/NotificationDrawer";
import { DRAWER_TYPES } from "Common/Constants";
import { ReportBug } from "Containers/ReportBug";
import { MyProfile } from "Containers/MyProfile";
import { useMemo } from "react";
import { GetStartedTips, PrivacyPolicy, TermsAndConditions } from "Containers/Home";

export const MainLayout = () => {
  const isAuthenticated = useIsAuthenticated();
  const { data: authDetails, isLoading: isLoadingAuthorization } = useGetAuthorizationQuery({}, { skip: !isAuthenticated });
  const authContextValue = useMemo(() => ({ authDetails, isLoadingAuthorization }), [authDetails, isLoadingAuthorization]);

  const {
    featuresDrawer,
    sessionStartTime,
    isMessageCenterEnabled,
    unreadCount,
    pendingSignalRItems,
    setPendingSignalRItems,
    handleFeaturesDrawerClose,
    handleReportBugDrawerOpen,
    handleProfileDrawerOpen,
    handleNotificationDrawerOpen,
    handleNotificationDismissed,
    handleNotificationMarkedUnread,
    handleAllNotificationsDismissed,

    isPrivacyPolicyOpen,
    openTermsAndConditions,
    openGetStartedTips,
    handleCloseDialog,
    handlePrivacyPolicyDialogOpen,
    handleTermsOfServiceDialogOpen,
    handleTipsDialogOpen,
    handleTermsCheckboxChange,
    handleAcceptTerms,
    isChecked,
    isLoadingTerms,
    hasAcceptedTerms,
    acceptedDate,
    isFinalStep,
    isStartingStep,
    setIsStartingStep,
    handleNextStep,
    handlePreviousStep,
    activeStep,
    startingTipImageUrl,
    termsOfUsePdfUrl,
    slides,
  } = useMainLayout();

  return (
    <AuthContext.Provider value={authContextValue}>
      <Box
        sx={{
          display: "flex",
          width: "100vw",
          overflow: "hidden",
        }}
      >
        <Box component="main" sx={{ width: "100%" }}>
          <Header
            handleProfileDrawerOpen={handleProfileDrawerOpen}
            handleReportBugDrawerOpen={handleReportBugDrawerOpen}
            handleNotificationDrawerOpen={handleNotificationDrawerOpen}
            handleTermsOfServiceDialogOpen={handleTermsOfServiceDialogOpen}
            handleTipsDialogOpen={handleTipsDialogOpen}
            handlePrivacyPolicyDialogOpen={handlePrivacyPolicyDialogOpen}
            isMessageCenterEnabled={isMessageCenterEnabled}
            unreadCount={unreadCount}
          />
          <Toolbar />
          <Box
            sx={(theme) => ({
              width: "100%",
              height: `calc(100dvh - ${theme.mixins.toolbar.minHeight}px)`,
              borderTop: `1px solid ${theme.palette.grey[200]}`,
            })}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>

      {isMessageCenterEnabled && (
        <NotificationDrawer
          open={featuresDrawer.type === DRAWER_TYPES.NOTIFICATION && featuresDrawer.open}
          onClose={handleFeaturesDrawerClose}
          sessionStartTime={sessionStartTime}
          pendingSignalRItems={pendingSignalRItems}
          onPendingItemsConsumed={() => setPendingSignalRItems([])}
          onDismissed={handleNotificationDismissed}
          onMarkedUnread={handleNotificationMarkedUnread}
          onAllDismissed={handleAllNotificationsDismissed}
        />
      )}

      <MyProfile
        open={featuresDrawer.type === DRAWER_TYPES.VIEW && featuresDrawer.open}
        handleClose={handleFeaturesDrawerClose}
      />

      <ReportBug
        open={featuresDrawer.type === DRAWER_TYPES.ADD && featuresDrawer.open}
        handleClose={handleFeaturesDrawerClose}
      />

      <PrivacyPolicy isOpen={isPrivacyPolicyOpen} onClose={handleCloseDialog} />

      <TermsAndConditions
        hasAcceptedTerms={hasAcceptedTerms}
        handleAcceptTerms={handleAcceptTerms}
        handleTermsCheckboxChange={handleTermsCheckboxChange}
        isChecked={isChecked}
        isLoading={isLoadingTerms}
        openTermsAndConditions={openTermsAndConditions}
        handleCloseTerms={handleCloseDialog}
        acceptedDate={acceptedDate}
        termsOfUsePdfUrl={termsOfUsePdfUrl}
      />

      <GetStartedTips
        openGetStartedTips={openGetStartedTips}
        isFinalStep={isFinalStep}
        isStartingStep={isStartingStep}
        setIsStartingStep={setIsStartingStep}
        handleCloseTips={handleCloseDialog}
        handleNextStep={handleNextStep}
        handlePreviousStep={handlePreviousStep}
        activeStep={activeStep}
        startingTipImageUrl={startingTipImageUrl}
        slides={slides}
      />
    </AuthContext.Provider>
  );
};
